import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useCustomizeState } from './hooks/use-customize-state';
import { SkeletonPreview } from './preview/SkeletonPreview';
import { ProfileEditorProvider, useProfileEditorRegistry } from './profile-editor-registry';
import { BrandAssetsSection } from './sections/BrandAssetsSection';
import { ColorSection } from './sections/ColorSection';
import { DividerSection } from './sections/DividerSection';
import { FontSection } from './sections/FontSection';
import { FooterSection } from './sections/FooterSection';
import { HeaderSection } from './sections/HeaderSection';
import { TableSection } from './sections/TableSection';
import type { ActiveSection } from './types';
import type { ReportData } from '@/components/preview/types';
import type { ReactNode } from 'react';

interface CustomizeModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    data: ReportData;
    onUpdate: (updates: Partial<ReportData> | ((prev: ReportData) => Partial<ReportData>)) => void;
    /** Expands this accordion on open; null leaves it as the user left it. */
    initialSection?: ActiveSection;
}

/**
 * Sections are grouped by reach, not by page anatomy: what the whole laudo looks
 * like, then what repeats on every page, then the blocks a laudo may not have.
 */
function SectionGroup({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="space-y-2">
            <span className="text-[0.625rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                {label}
            </span>
            <div className="space-y-2">{children}</div>
        </div>
    );
}

export default function CustomizeModal({
    open,
    onOpenChange,
    data,
    onUpdate,
    initialSection = null,
}: CustomizeModalProps) {
    const state = useCustomizeState({ data, onUpdate, onOpenChange, open, initialSection });
    const profileEditors = useProfileEditorRegistry();

    // The two block-scoped sections say so when this laudo has nothing to apply
    // them to. `tests.blocks` is the only place blocks live — custom sections
    // carry HTML content, never blocks.
    const { tableBlockCount, dividerBlockCount } = useMemo(() => {
        const blocks = data.tests?.blocks ?? [];
        return {
            tableBlockCount: blocks.filter((block) => block.block_type === 'table').length,
            dividerBlockCount: blocks.filter((block) => block.block_type === 'divider').length,
        };
    }, [data.tests?.blocks]);

    const coverColors = data.customizations?.cover?.colors;
    const coverOverridesColors = !!(coverColors?.primary || coverColors?.secondary);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="h-[85vh] max-w-5xl gap-0 overflow-hidden p-0"
                onEscapeKeyDown={(event: KeyboardEvent) => {
                    // Radix handles Escape on the document in the capture phase,
                    // so an open profile editor cannot stop it from below — the
                    // dialog declines here instead, or a half-typed CRP is lost.
                    if (profileEditors.closeAll()) event.preventDefault();
                }}
            >
                <ProfileEditorProvider registry={profileEditors}>
                    <div className="flex h-full overflow-hidden">
                        <div className="flex w-1/2 flex-col overflow-hidden border-r border-border">
                            <div className="flex-shrink-0 border-b border-border px-6 py-4">
                                <DialogTitle className="text-lg font-semibold">
                                    Customize o seu laudo
                                </DialogTitle>
                                {/* Screen-reader only: the visible subtitle was dropped on
                                purpose, but the dialog still owes assistive tech a
                                description. */}
                                <DialogDescription className="sr-only">
                                    Ajuste as cores, a tipografia, a logo, o cabeçalho, o rodapé e
                                    os blocos de resultados do laudo. O preview ao lado acompanha
                                    cada mudança.
                                </DialogDescription>
                            </div>

                            <ScrollArea className="flex-1 overflow-y-auto">
                                <div className="space-y-6 px-6 py-4">
                                    <SectionGroup label="Sua marca">
                                        <ColorSection
                                            open={state.activeSection === 'colors'}
                                            onToggle={() => state.toggleSection('colors')}
                                            localPrimary={state.localPrimary}
                                            localSecondary={state.localSecondary}
                                            setLocalPrimary={state.setLocalPrimary}
                                            setLocalSecondary={state.setLocalSecondary}
                                            onPresetClick={state.handlePresetClick}
                                            tableConfig={state.localTableConfig}
                                            updateTableConfig={state.updateTableConfig}
                                            dividerConfig={state.localDividerConfig}
                                            updateDividerConfig={state.updateDividerConfig}
                                            coverOverridesColors={coverOverridesColors}
                                            colorTarget={state.colorTarget}
                                            onColorTargetChange={state.setColorTarget}
                                        />

                                        <FontSection
                                            open={state.activeSection === 'font'}
                                            onToggle={() => state.toggleSection('font')}
                                            localFont={state.localFont}
                                            setLocalFont={state.setLocalFont}
                                        />

                                        <BrandAssetsSection
                                            open={state.activeSection === 'brand'}
                                            onToggle={() => state.toggleSection('brand')}
                                            config={state.localHeaderConfig}
                                            updateConfig={state.updateHeaderConfig}
                                            logoInputRef={state.logoInputRef}
                                            letterheadInputRef={state.letterheadInputRef}
                                            logoUploadState={state.logoUploadState}
                                            letterheadUploadState={state.letterheadUploadState}
                                            onFileUpload={state.handleFileUpload}
                                            onDeleteImage={state.handleDeleteImage}
                                        />
                                    </SectionGroup>

                                    <SectionGroup label="Cada página">
                                        <HeaderSection
                                            open={state.activeSection === 'header'}
                                            onToggle={() => state.toggleSection('header')}
                                            config={state.localHeaderConfig}
                                            updateConfig={state.updateHeaderConfig}
                                            hasCrp={state.hasCrp}
                                        />

                                        <FooterSection
                                            open={state.activeSection === 'footer'}
                                            onToggle={() => state.toggleSection('footer')}
                                            config={state.localFooterConfig}
                                            updateConfig={state.updateFooterConfig}
                                            hasPhone={state.hasPhone}
                                            hasInstagram={state.hasInstagram}
                                        />
                                    </SectionGroup>

                                    <SectionGroup label="Blocos de resultados">
                                        <TableSection
                                            open={state.activeSection === 'table'}
                                            onToggle={() => state.toggleSection('table')}
                                            config={state.localTableConfig}
                                            updateConfig={state.updateTableConfig}
                                            blockCount={tableBlockCount}
                                        />

                                        <DividerSection
                                            open={state.activeSection === 'divider'}
                                            onToggle={() => state.toggleSection('divider')}
                                            config={state.localDividerConfig}
                                            updateConfig={state.updateDividerConfig}
                                            blockCount={dividerBlockCount}
                                        />
                                    </SectionGroup>
                                </div>
                            </ScrollArea>

                            <div className="flex flex-shrink-0 justify-end gap-3 border-t border-border px-6 py-4">
                                <Button variant="outline" onClick={state.handleCancel}>
                                    Cancelar
                                </Button>
                                <Button onClick={state.handleApply}>Aplicar</Button>
                            </div>
                        </div>

                        {/* Right Side - Skeleton Preview */}
                        <SkeletonPreview
                            activeSection={state.activeSection}
                            colorTarget={state.colorTarget}
                            headerConfig={state.localHeaderConfig}
                            footerConfig={state.localFooterConfig}
                            tableConfig={state.localTableConfig}
                            dividerConfig={state.localDividerConfig}
                            primaryColor={state.localPrimary}
                            secondaryColor={state.localSecondary}
                            fontFamily={state.localFont}
                            psychologistName={state.psychologistName}
                            psychologistCrp={state.psychologistCrp}
                        />
                    </div>
                </ProfileEditorProvider>
            </DialogContent>
        </Dialog>
    );
}
